import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { useIsTouchDevice } from "../../hooks/useIsTouchDevice";
import "./CustomCursor.css";

const INTERACTIVE_SELECTOR = 'a, button, [data-cursor="hover"], input, textarea';

export function CustomCursor() {
  const isTouch = useIsTouchDevice();
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(false);
  const rafReady = useRef(false);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const springX = useSpring(x, { damping: 28, stiffness: 320, mass: 0.4 });
  const springY = useSpring(y, { damping: 28, stiffness: 320, mass: 0.4 });

  useEffect(() => {
    if (isTouch) return;
    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    if (prefersReduced) return;

    const onMove = (e: MouseEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
      if (!rafReady.current) {
        rafReady.current = true;
        setVisible(true);
      }
      const target = e.target as HTMLElement;
      setHovering(Boolean(target.closest(INTERACTIVE_SELECTOR)));
    };

    const onLeave = () => setVisible(false);

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [isTouch, x, y]);

  if (isTouch) return null;

  return (
    <motion.div
      className="custom-cursor"
      data-hovering={hovering}
      style={{
        translateX: springX,
        translateY: springY,
        opacity: visible ? 1 : 0,
      }}
      aria-hidden="true"
    />
  );
}
